import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  HandThumbUpIcon as ThumbUpSolid,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/solid';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const EmployerReviewsPage = () => {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    fetchEmployerProfile();
    fetchRatingStats();
    fetchReviews();
  }, [profileId]);

  const fetchEmployerProfile = async () => {
    try {
      const response = await fetch(`${API}/employer-profiles/${profileId}`);
      if (response.ok) {
        const data = await response.json();
        setEmployerProfile(data);
      } else {
        toast.error('事業者プロフィールが見つかりません');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching employer profile:', error);
      toast.error('データの取得に失敗しました');
      navigate('/');
    }
  };

  const fetchRatingStats = async () => {
    try {
      const response = await fetch(`${API}/employer-profiles/${profileId}/rating-stats`);
      if (response.ok) {
        const data = await response.json();
        setRatingStats(data);
      }
    } catch (error) {
      console.error('Error fetching rating stats:', error);
    }
  };

  const fetchReviews = async (skip = 0, limit = 20) => {
    try {
      if (skip === 0) setLoading(true);
      else setLoadingMore(true);

      const response = await fetch(`${API}/employer-profiles/${profileId}/reviews?skip=${skip}&limit=${limit}`);
      if (response.ok) {
        const data = await response.json();
        if (skip === 0) {
          setReviews(data.reviews || []);
        } else {
          setReviews(prev => [...prev, ...(data.reviews || [])]);
        }
        setHasMore(data.has_more || false);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
      toast.error('レビューの取得に失敗しました');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreReviews = () => {
    if (!loadingMore && hasMore) {
      fetchReviews(reviews.length, 20);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ヘッダー */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">店舗の評価</h1>
              {employerProfile && (
                <p className="text-sm text-gray-500">{employerProfile.business_name}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* 評価統計 */}
        {ratingStats && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <ThumbUpSolid className="h-12 w-12 text-yellow-400 mr-3" />
                <div>
                  <div className="text-4xl font-bold text-gray-900">
                    {ratingStats.good_percentage}%
                  </div>
                  <div className="text-sm text-gray-500">Good率</div>
                </div>
              </div>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <span className="mr-1"></span>
                <span>この数字について</span>
              </div>
            </div>
          </div>
        )}

        {/* レビュー一覧 */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" />
              レビュー
              {ratingStats && (
                <span className="ml-2 text-sm text-gray-500">
                  ({ratingStats.total_reviews}件)
                </span>
              )}
            </h2>
          </div>

          <div className="divide-y divide-gray-100">
            {reviews.length === 0 ? (
              <div className="p-8 text-center">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">まだレビューがありません</p>
              </div>
            ) : (
              reviews.map((review, index) => (
                <div key={review.id || index} className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-sm">
                          {review.reviewer_age_group ? review.reviewer_age_group.charAt(0) : '匿'}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-2">
                        <span className="font-medium text-sm text-gray-900">
                          {review.reviewer_age_group || '匿名'} {review.reviewer_gender || ''}
                        </span>
                        <span className="mx-2 text-gray-300"></span>
                        <span className="text-xs text-gray-500">
                          {formatDate(review.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center mb-2">
                        <ThumbUpSolid className="h-4 w-4 text-yellow-400 mr-1" />
                        <span className="text-sm font-medium text-gray-900">グッド</span>
                      </div>
                      {review.comment && (
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {review.comment}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* もっと読み込むボタン */}
          {hasMore && reviews.length > 0 && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={loadMoreReviews}
                disabled={loadingMore}
                className="w-full py-3 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loadingMore ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    読み込み中...
                  </>
                ) : (
                  'さらに読み込む'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployerReviewsPage;